import React, { useMemo, useState } from "react";
import { cn } from "../../lib/cn";

export function Option({ value, children }) {
  return null;
}

Option._isOption = true;

/**
 * Ant Design–like Select using Option children.
 * Supports `showSearch` + `filterOption(input, option)`.
 */
export function Select({
  value,
  onChange,
  children,
  className,
  placeholder,
  allowClear,
  showSearch,
  filterOption,
  mode,
  disabled,
  style,
  defaultValue,
  optionFilterProp,
  ...rest
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultValue);
  const isControlled = value !== undefined;
  const selected = isControlled ? value : uncontrolled;

  const options = useMemo(() => {
    return React.Children.toArray(children)
      .filter(
        (child) =>
          React.isValidElement(child) &&
          child.type &&
          (child.type._isOption ||
            child.type === Option ||
            child.type.displayName === "Option")
      )
      .map((child) => ({
        value: child.props.value,
        label: child.props.children,
      }));
  }, [children]);

  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!showSearch || !q) return options.filter(Boolean);
    return options.filter((o) => {
      if (!filterOption) {
        return String(o.label).toLowerCase().includes(q.toLowerCase());
      }
      return filterOption(q, {
        children: o.label,
        value: o.value,
      });
    });
  }, [options, q, showSearch, filterOption]);

  if (mode === "multiple") {
    /* minimal: native multi select */
    const arr = Array.isArray(selected) ? selected : [];
    return (
      <select
        multiple
        disabled={disabled}
        value={arr}
        style={style}
        className={cn(
          "w-full min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20",
          className
        )}
        onChange={(e) => {
          const vals = [...e.target.selectedOptions].map((o) => o.value);
          if (!isControlled) setUncontrolled(vals);
          onChange?.(vals);
        }}
        {...rest}
      >
        {options.map((o) => (
          <option key={String(o.value)} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className={cn("relative w-full", className)} style={style}>
      {showSearch ? (
        <input
          type="text"
          disabled={disabled}
          placeholder={placeholder || "Search…"}
          className="mb-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      ) : null}
      <select
        disabled={disabled}
        value={
          selected === undefined || selected === null ? "" : String(selected)
        }
        className={cn(
          "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm capitalize outline-none focus:border-brand focus:ring-2 focus:ring-brand/20",
          optionFilterProp && "capitalize"
        )}
        onChange={(e) => {
          let v = e.target.value === "" ? undefined : e.target.value;
          try {
            const orig = options.find((o) => String(o.value) === String(v));
            if (
              orig &&
              typeof orig.value === "number" &&
              typeof v === "string" &&
              v !== ""
            ) {
              v = Number(v);
            }
          } catch {
            /* keep string */
          }
          if (!isControlled) setUncontrolled(v);
          onChange?.(v, {});
        }}
        {...rest}
      >
        {(placeholder || allowClear) && !showSearch ? (
          <option value="">{placeholder || "Select…"}</option>
        ) : null}
        {(showSearch ? filtered : options).map((o) => (
          <option key={String(o.value)} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

Select.Option = Option;
